import { createNewObjects } from 'components/FormElements/TagForm/tagFormHelpers';
import { openDB } from 'idb';
import SyntaxHighlighter from "react-syntax-highlighter";
import { dracula } from "react-syntax-highlighter/dist/esm/styles/hljs";

export const returnDescriptionWithSyntaxHighlightedCodeBlocks = (html) => {

  const appendCodeBlockAndHTMLBeforeIt = (numberOfCodeBlocks) => {
    let codeToReturn = [];
    const description = html;
    const indexOfBeginningOfFirstOpeningPreTag = description.indexOf('<pre>');
    const indexOfEndOfFirstClosingPreTag = description.indexOf('</pre>');
    let indexOfBeginningOfMostRecentlyFoundOpeningPreTag = indexOfBeginningOfFirstOpeningPreTag;
    let indexOfEndOfMostRecentlyFoundClosingPreTag = indexOfEndOfFirstClosingPreTag;

    for (let i = 0; i < numberOfCodeBlocks; i++) {
      let startingAfterTheLastCodeBlockFound = indexOfBeginningOfMostRecentlyFoundOpeningPreTag + (i + 1);
      const indexOfBeginningOfNextOpeningPreTag = description.indexOf('<pre>', startingAfterTheLastCodeBlockFound);
      indexOfBeginningOfMostRecentlyFoundOpeningPreTag = indexOfBeginningOfNextOpeningPreTag;
      const indexOfBeginningOfNextOpeningCodeTag = description.indexOf('<code', startingAfterTheLastCodeBlockFound);
      const indexOfBeginningOfNextClosingCodeTag = description.indexOf('>', indexOfBeginningOfNextOpeningCodeTag);
      let indexOfEndOfNextClosingPreTag = description.indexOf('</pre>', indexOfEndOfMostRecentlyFoundClosingPreTag + (i + 1));
      indexOfEndOfMostRecentlyFoundClosingPreTag = indexOfEndOfNextClosingPreTag + 6;
      const indexOfFirstCharacterInsideNextCodeTag = description.indexOf('>', indexOfBeginningOfNextOpeningCodeTag) + 1;
      const indexOfLastCharacterInsideNextCodeTag = description.indexOf('</code>', indexOfBeginningOfNextOpeningCodeTag);
      const codeBlock = description.substring(indexOfFirstCharacterInsideNextCodeTag, indexOfLastCharacterInsideNextCodeTag);
      const indexOfEndOfPreviousClosingPreTag = description.indexOf('</pre>', indexOfLastCharacterInsideNextCodeTag);
      const htmlAfterCodeBlock = description.substring(indexOfEndOfPreviousClosingPreTag + 6, indexOfBeginningOfNextOpeningPreTag);
      const codeTag = description.substring(indexOfBeginningOfNextOpeningCodeTag, indexOfBeginningOfNextClosingCodeTag + 1);
      const fitContent = codeTag.includes('fit-content');
      let language;
      switch(true) {
        case codeTag.includes('language-css'):
          language = 'css';
          break;
        case codeTag.includes('language-html'):
          language = 'html';
          break;
        case codeTag.includes('language-js'):
          language = 'javascript';
          break;
        case codeTag.includes('language-json'):
          language = 'json';
          break;
        case codeTag.includes('language-php'):
          language = 'php';
          break;
        case codeTag.includes('language-scss'):
          language = 'scss';
          break;
        case codeTag.includes('language-ts'):
          language = 'typescript';
          break;
        default:
          language = null;
      }
      
      const codeBlockWithSyntaxHighlighter = (
        <SyntaxHighlighter key={i} language={language} style={dracula} customStyle={fitContent === true ? {width: 'fit-content'} : ''}>
          {codeBlock}
        </SyntaxHighlighter>
      );
      const highlightedCodeBlock = codeBlockWithSyntaxHighlighter;
      let renderedHTMLAfterCodeBlock = null;
      if (indexOfBeginningOfNextOpeningPreTag !== -1) {
        renderedHTMLAfterCodeBlock = <div key={i + 10} dangerouslySetInnerHTML={{ __html: htmlAfterCodeBlock }}></div>;
      }
      codeToReturn.push(highlightedCodeBlock, renderedHTMLAfterCodeBlock);
    }
    return codeToReturn;
  }

  const description = html;
  const numberOfCodeBlocks = description.split("<pre>").length - 1;
  const beginningOfFirstOpeningPreTag = description.indexOf('<pre>');
  const endOfLastClosingPreTag = description.lastIndexOf('</pre>');
  const firstSectionOfDescriptionHTML = description.substring(0, beginningOfFirstOpeningPreTag);
  const lastSectionOfDescriptionHTML = description.substring(endOfLastClosingPreTag + 6);

  if (numberOfCodeBlocks === 0) {
    return (
      <div className='description description--html' dangerouslySetInnerHTML={{ __html: description }}></div>
    )
  }

  return (
    <div className='description description--html'>
      <div dangerouslySetInnerHTML={{ __html: firstSectionOfDescriptionHTML }}></div>
      {appendCodeBlockAndHTMLBeforeIt(numberOfCodeBlocks)}
      <div dangerouslySetInnerHTML={{ __html: lastSectionOfDescriptionHTML }}></div>
    </div>
  );
};

export const allowOnlyNumbers = (e) => {
  const eventCode = e.code.toLowerCase();
  if (!(e.code !== null
    && (eventCode.includes("digit")
      || eventCode.includes("arrow")
      || eventCode.includes("home")
      || eventCode.includes("end")
      || eventCode.includes("backspace")
      || (eventCode.includes("numpad") && eventCode.length === 7)))
  ) {
    e.preventDefault();
  }
};

export const resetScrollPosition = (classSelector) => {
  const listItems = document.querySelectorAll(classSelector);
  for (let item of listItems) {
    item.scrollLeft = -999999;
  }
}

const fetchFileUrl = async (fileKey) => {
  if (!fileKey) return null;
  const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/get-file-url?fileName=${encodeURIComponent(fileKey)}`);
  if (!res.ok) throw new Error('Failed to fetch file URL');
  const data = await res.json();
  return data.url;
};

// TODO: Delete if images are working fine
// export const resolveFiles = async (files, folder) => {
//   if (!files?.length) return [];
//   return Promise.all(
//     files.filter(Boolean).map(async (file) => {
//       let fileKey = file.fileKey;
//       let thumbKey = file.thumbKey;
//       if (folder && fileKey && !fileKey.startsWith(`${folder}/`)) fileKey = `${folder}/${fileKey}`;
//       if (folder && thumbKey && !thumbKey.startsWith(`${folder}/`)) thumbKey = `${folder}/${thumbKey}`;
//       const fileUrl = fileKey ? await fetchFileUrl(fileKey) : null;
//       const thumbUrl = thumbKey ? await fetchFileUrl(thumbKey) : null;
//       return { ...file, fileUrl, thumbUrl };
//     })
//   );
// };

// Updated to work with images/files that may be older and were stored in 
// the root directory instead of the feature folder, ie
// Amazon S3 > Buckets > r.jessewhite.me-dev > flashCards/
export const resolveFiles = async (files, folder) => {
  if (!files?.length) return [];

  return Promise.all(
    files.filter(Boolean).map(async (file) => {
      let fileKey  = file.fileKey;
      let thumbKey = file.thumbKey;
      
      // Try with folder first
      let prefixedFileKey  = folder && fileKey  && !fileKey.startsWith(`${folder}/`) ? `${folder}/${fileKey}` : fileKey;
      let prefixedThumbKey = folder && thumbKey && !thumbKey.startsWith(`${folder}/`) ? `${folder}/${thumbKey}` : thumbKey;
      
      let fileUrl  = null;
      let thumbUrl = null;
            
      try {
        fileUrl = prefixedFileKey ? await fetchFileUrl(prefixedFileKey) : null;
      } catch {
        fileUrl = fileKey ? await fetchFileUrl(fileKey) : null;
      }
      
      try {
        thumbUrl = prefixedThumbKey ? await fetchFileUrl(prefixedThumbKey) : null;
      } catch {
        thumbUrl = thumbKey ? await fetchFileUrl(thumbKey) : null;
      }

      return { ...file, fileUrl, thumbUrl };
    })
  );
};

export const createNewTagEntities = async (token, sendRequest, tagsList) => {
  const newTagObjects = [...createNewObjects(tagsList)];

  await Promise.all(newTagObjects.map(async (newObject) => {
    return sendRequest(
      `${process.env.REACT_APP_BACKEND_URL}/tags/`,
      "POST",
      JSON.stringify(newObject),
      {
        "Content-Type": "application/json",
        Authorization: "Bearer " + token,
      }
    );
  }));
};

const getDB = async () => {
  return openDB('image-cache-db', 1, { upgrade(db) { db.createObjectStore('images') } });
};

export const getImageFromCache = async (key) => {
  const db = await getDB();
  return db.get('images', key);
};

export const saveImageToCache = async (key, blob) => {
  const db = await getDB();
  return db.put('images', blob, key);
};